import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OnInit,ElementRef, ViewChild,OnDestroy } from '@angular/core';
import { MovieDetailsService } from '../../../../core/services/movie/movie.service';
import { LanguageService } from '../../../../core/services/language/language.service';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { MovieTrailer } from '../movie-trailer/movie-trailer.component';
import { Inject,PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { YoutubePlayerService } from '../../../../core/services/youtube.service';
import { TeaserTrailerService } from '../../../../core/services/trailers.service';
import ColorThief from 'colorthief';
import { Input,Output,EventEmitter } from '@angular/core';
import { Utils } from '../../../../shared/utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SeoService } from '../../../../core/services/seo/seo.service';


@Component({

  selector: 'app-movie-details',
  imports: [CommonModule],
  templateUrl: './movie-details.html',
  styleUrl: './movie-details.scss'
})

export class MovieDetails implements OnInit,OnDestroy {
  private seoService = inject(SeoService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private movieDetailsService: MovieDetailsService,
    private route: ActivatedRoute,
    private youtubeService: YoutubePlayerService,
    private trailerService: TeaserTrailerService,
    private utils:Utils
  ){}
  
  @ViewChild('movieDetailContainer') movieDetailContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('movieBackdropImage') movieBackdropImage!: ElementRef<HTMLImageElement>;
  @ViewChild('backdropOverlay') backdropOverlay!: ElementRef<HTMLDivElement>;
  @ViewChild('movieTrailer') movieTrailer !: MovieTrailer;
  @ViewChild('trailerPlayerContainer') trailerPlayerContainer !: ElementRef<HTMLDivElement>;
  
  // Remove trailerDiv input - we'll find it via DOM
  @Input() showPlayer: boolean = false;
  @Output() showPlayerChange = new EventEmitter<boolean>();
    
  movieId: string = '' ;
  movieName: string | null = null;
  movieData: any = null;
  isLoading: boolean = true;
  posterLoaded: boolean = false;
  imageUrl: string | null = null;
  bgColor: string | null = null;
  frontendUrl:any = environment.frontendUrl;
  backendServer: string = environment.backendUrl;  
  trailerId: string = "";
  dominantColor:string = '';
  palette : any[] = [];
  middleColor: string = '';
  encodeURIComponent = encodeURIComponent;

  public readonly languageService = inject(LanguageService);
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const slug = params.get('slug');
      this.fetchMovieDetails(slug);
    });

    this.languageService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.movieId) {
        this.fetchMovieDetails();
      }
    });
  }


  onImageLoad(img: HTMLImageElement) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const colorThief = new ColorThief();
      const color = colorThief.getColor(img);
      this.dominantColor = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

      const palette = colorThief.getPalette(img, 5);
      this.palette = palette.map((c: any) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`);

      const middleIndex = Math.floor(this.palette.length / 2);
      this.middleColor = this.palette[middleIndex];
    } catch (e) {
      console.warn('ColorThief could not extract colors from backdrop image:', e);
    }
  }
  

  fetchMovieDetails(slugParam?: string | null) {
    const slug = slugParam !== undefined ? slugParam : this.route.snapshot.paramMap.get('slug');
    
    if (slug) {
      const [id, name] = slug.split('-');
      if (this.movieId !== id) {
        this.isLoading = true;
        this.posterLoaded = false;
        this.movieData = null;
      }
      this.movieId = id;  
      this.movieName = name;
    }
  
    if (this.movieId) {
      this.isLoading = true;
      const lang = this.languageService.currentLanguage?.code || 'en-US';
      this.movieDetailsService.getMovieDetails(parseInt(this.movieId), 'movie', lang).subscribe({
        next: (data) => {
          this.movieData = data;
          this.isLoading = false;
          const currentSlug = slug || this.movieId;
          this.seoService.setMovieSeo(data, currentSlug);
          if (isPlatformBrowser(this.platformId)) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        },
        error: (err) => {
          console.error('Failed to load movie details:', err);
          this.isLoading = false;
        }
      });
    }
  }

  async playTrailer(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.trailerService
      .getTeaserOrTrailers(parseInt(this.movieId), 'movie')
      .subscribe(trailers => {
        const trailer = trailers.results.find((t: any) =>
          t.type === 'Trailer' &&
          (t.official === true)
        );

        if (trailer) {
          this.trailerId = trailer.key;
          
          // Emit to parent to show modal
          this.showPlayer = true;
          this.showPlayerChange.emit(true);
          
          // Prevent body scroll
          document.body.style.overflow = 'hidden';

          // Wait for modal to render, then find the element directly
          setTimeout(() => {
            const trailerElement = document.getElementById('trailer-player');
            
            if (trailerElement) {
              this.youtubeService.playTrailer(this.trailerId, 'trailer-player');
            }
          }, 400);
          
        } else {
          this.showPlayer = false;
          this.showPlayerChange.emit(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.youtubeService.destroyPlayer();
  }

  onImageError(event: Event) {
    this.posterLoaded = true;
    return this.utils.onImageVerticalError(event);
  }


  filterRating(rating:number){
    return this.utils.filerRatings(rating ?? 0);
  }

  triptContent(content: string | null | undefined) {
    return this.utils.tripContent(content);
  }

}
